/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ConfirmationDialog } from "../../dialogs/ConfirmationDialog/ConfirmationDialog";
import { IEditor } from "../../IEditor";
import { TOAST_TYPE } from "../../toasts/IToast";
import { ConfigEditorEdit } from "./ConfigEditorEdit/ConfigEditorEdit";
import { ErrorTracker } from "@adapptio/lib-components";

export function registerCommands(dc: IEditor): void {
	const viewSvc = dc.get("viewService");
	const commandsSvc = dc.get("commandsService");
	const toastSvc = dc.get("toastService");
	const dialogSvc = dc.get("dialogService");

	commandsSvc.register({
		id: "appConfig.open",
		label: "Open manifest editor",
		args: [],
		returns: "none",
		handler: async () => {
			viewSvc.open("appConfig", "appConfig", null, true);
		}
	});

	commandsSvc.register({
		id: "appConfig.publicKey.copy",
		label: "Copy SSH public key",
		args: [
			{
				label: "Public key",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const publicKey = args[0] as string;
			navigator.clipboard.writeText(publicKey);
			toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				message: "Public key copy!",
				timeoutMs: 2500
			});
		}
	});

	commandsSvc.register({
		id: "configEditor.edit.save",
		label: "Save manifest",
		handler: async () => {
			if (viewSvc.isViewActive("appConfig")) {
				const configEditor = viewSvc.getCurrentView() as ConfigEditorEdit;

				const viewInfo = configEditor.getViewInfo();

				if (!viewInfo.isModified) {
					return;
				}

				await dialogSvc.open(ConfirmationDialog, "config.save.confirm", {
					title: "Save configuration",
					message: "This might take a while. Do you wish to continue?",
					icon: "mdi/alert",
					confirmLabel: "Save configuration",
					confirmIcon: "mdi/content-save-cog",
					doConfirm: async () => {
						try {
							await configEditor.save();
						} catch (err) {
							const errorTracker = ErrorTracker.getInstance();
							errorTracker.captureException(err);
						}
					}
				});
			}
		}
	});

	commandsSvc.register({
		id: "configEditor.edit.undo",
		label: "Undo",
		handler: async () => {
			if (viewSvc.isViewActive("appConfig")) {
				const configEditor = viewSvc.getCurrentView() as ConfigEditorEdit;

				if (configEditor.canUndo()) {
					configEditor.undo();
				}
			}
		}
	});

	commandsSvc.register({
		id: "configEditor.edit.redo",
		label: "Redo",
		handler: async () => {
			if (viewSvc.isViewActive("appConfig")) {
				const configEditor = viewSvc.getCurrentView() as ConfigEditorEdit;

				if (configEditor.canRedo()) {
					configEditor.redo();
				}
			}
		}
	});
}
